"use client"

import { useState, useEffect } from "react"
import { Bell, BellOff, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useToast } from "@/components/ui/use-toast"
import {
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  isSubscribed,
  canUsePushNotifications,
  isIOSDevice,
  isInstalledPWA,
} from "@/lib/push-notifications"

interface PushPermissionPromptProps {
  onDismiss?: () => void
  autoShow?: boolean
}

export function PushPermissionPrompt({ onDismiss, autoShow = true }: PushPermissionPromptProps) {
  const [visible, setVisible] = useState(false)
  const [subscribed, setSubscribed] = useState(false)
  const [loading, setLoading] = useState(false)
  const { toast } = useToast()

  // Check subscription status on mount
  useEffect(() => {
    const checkSubscription = async () => {
      const subscriptionStatus = await isSubscribed()
      setSubscribed(subscriptionStatus)

      // Auto-show prompt if:
      // 1. Not subscribed
      // 2. Push is supported
      // 3. autoShow is enabled
      // 4. User hasn't dismissed it recently (check localStorage)
      if (autoShow && !subscriptionStatus && canUsePushNotifications()) {
        const dismissed = localStorage.getItem("push-prompt-dismissed")
        const dismissedDate = dismissed ? new Date(dismissed) : null
        const daysSinceDismissed = dismissedDate
          ? (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24)
          : 999

        // Show if never dismissed or dismissed more than 7 days ago
        if (daysSinceDismissed > 7) {
          setVisible(true)
        }
      }
    }

    checkSubscription()
  }, [autoShow])

  const handleSubscribe = async () => {
    setLoading(true)
    try {
      await subscribeToPushNotifications()
      setSubscribed(true)
      setVisible(false)

      toast({
        title: "Notifikace zapnuty",
        description: "Budeme ti připomínat 2× denně (ráno v 10:00 a večer v 18:00), abys si zapsal/a svou zónu.",
      })
    } catch (error) {
      console.error("Failed to subscribe:", error)

      // Show specific error messages
      const errorMessage = error instanceof Error ? error.message : "Neznámá chyba"
      let description = "Zkus to prosím znovu později."

      if (errorMessage.includes("permission denied")) {
        description = "Povol notifikace v nastavení prohlížeče a zkus to znovu."
      } else if (errorMessage.includes("not supported")) {
        description = "Tvůj prohlížeč nepodporuje push notifikace."
      }

      toast({
        title: "Nelze zapnout notifikace",
        description,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleUnsubscribe = async () => {
    setLoading(true)
    try {
      await unsubscribeFromPushNotifications()
      setSubscribed(false)

      toast({
        title: "Notifikace vypnuty",
        description: "Už tě nebudeme obtěžovat denními připomínkami.",
      })
    } catch (error) {
      console.error("Failed to unsubscribe:", error)

      toast({
        title: "Nelze vypnout notifikace",
        description: "Zkus to prosím znovu později.",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDismiss = () => {
    setVisible(false)
    localStorage.setItem("push-prompt-dismissed", new Date().toISOString())
    onDismiss?.()
  }

  // Don't render if push is not supported
  if (!canUsePushNotifications()) {
    // Show iOS installation prompt instead
    if (isIOSDevice() && !isInstalledPWA()) {
      return null // iOS banner component will handle this
    }
    return null
  }

  // Don't render if not visible
  if (!visible) {
    return null
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 z-40 sm:left-auto sm:right-4 sm:max-w-md">
      <div className="rounded-lg border border-border bg-card p-4 shadow-lg">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <Bell className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 space-y-2">
            {subscribed ? (
              <>
                <h3 className="font-medium text-sm">Denní připomínky zapnuty</h3>
                <p className="text-sm text-muted-foreground">
                  Každý den v 10:00 a 18:00 ti připomeneme, abys si zapsal/a svou zónu.
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={handleUnsubscribe}
                    disabled={loading}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    <BellOff className="mr-2 h-4 w-4" />
                    {loading ? "Vypínám..." : "Vypnout připomínky"}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <h3 className="font-medium text-sm">Denní připomínky</h3>
                <p className="text-sm text-muted-foreground">
                  Chceš dostávat denní připomínku, abys si zapsal/a svou zónu?
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={handleSubscribe}
                    disabled={loading}
                    size="sm"
                    className="flex-1"
                  >
                    <Bell className="mr-2 h-4 w-4" />
                    {loading ? "Zapínám..." : "Zapnout"}
                  </Button>
                  <Button
                    onClick={handleDismiss}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    Později
                  </Button>
                </div>
              </>
            )}
          </div>
          {!subscribed && (
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
